import React from 'react';

interface AttendanceHeatmapProps {
  userAttendanceData: { [key: string]: number };
  userName: string;
}

const AttendanceHeatmap: React.FC<AttendanceHeatmapProps> = ({ userAttendanceData, userName }) => {
  // Generate calendar grid for the current year or date range
  const generateCalendarGrid = () => {
    const today = new Date();
    const startDate = new Date(today.getFullYear(), 0, 1); // Start of current year
    const endDate = new Date(today.getFullYear(), 11, 31); // End of current year
    
    const weeks: Date[][] = [];
    let currentWeek: Date[] = [];
    
    // Start from the beginning of the week containing startDate
    const startOfWeek = new Date(startDate);
    startOfWeek.setDate(startDate.getDate() - startDate.getDay());
    
    let currentDate = new Date(startOfWeek);
    
    while (currentDate <= endDate) {
      currentWeek.push(new Date(currentDate));
      
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // Add remaining days if any
    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }
    
    return weeks;
  };
  
  const getIntensityLevel = (date: Date): number => {
    const dateStr = date.toISOString().split('T')[0];
    const attendanceCount = userAttendanceData[dateStr] || 0;
    
    if (attendanceCount === 0) return 0;
    if (attendanceCount === 1) return 1;
    if (attendanceCount === 2) return 2;
    return 3; // 3 or more
  };
  
  const getIntensityClass = (level: number): string => {
    switch (level) {
      case 0: return 'heatmap-cell-empty';
      case 1: return 'heatmap-cell-low';
      case 2: return 'heatmap-cell-medium';
      case 3: return 'heatmap-cell-high';
      default: return 'heatmap-cell-empty';
    }
  };
  
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  const weeks = generateCalendarGrid();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  return (
    <div className="attendance-heatmap">
      <div className="heatmap-header">
        <h6>Attendance Heatmap for {userName}</h6>
        <div className="heatmap-legend">
          <span className="legend-label">Less</span>
          <div className="legend-cells">
            <div className="legend-cell heatmap-cell-empty"></div>
            <div className="legend-cell heatmap-cell-low"></div>
            <div className="legend-cell heatmap-cell-medium"></div>
            <div className="legend-cell heatmap-cell-high"></div>
          </div>
          <span className="legend-label">More</span>
        </div>
      </div>
      
      <div className="heatmap-container">
        <div className="heatmap-months">
          {monthNames.map((month, idx) => (
            <div key={idx} className="heatmap-month">{month}</div>
          ))}
        </div>
        
        <div className="heatmap-grid">
          <div className="heatmap-weekdays">
            <div className="weekday-label">Sun</div>
            <div className="weekday-label">Mon</div>
            <div className="weekday-label">Tue</div>
            <div className="weekday-label">Wed</div>
            <div className="weekday-label">Thu</div>
            <div className="weekday-label">Fri</div>
            <div className="weekday-label">Sat</div>
          </div>
          
          <div className="heatmap-weeks">
            {weeks.map((week, weekIdx) => (
              <div key={weekIdx} className="heatmap-week">
                {week.map((day, dayIdx) => {
                  const intensityLevel = getIntensityLevel(day);
                  const attendanceCount = userAttendanceData[day.toISOString().split('T')[0]] || 0;
                  
                  return (
                    <div
                      key={dayIdx}
                      className={`heatmap-cell ${getIntensityClass(intensityLevel)}`}
                      title={`${formatDate(day)}: ${attendanceCount} ${attendanceCount === 1 ? 'visit' : 'visits'}`}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceHeatmap; 
